import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import {
  Button,
  Col,
  Container,
  Modal,
  Row,
  Table,
} from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { eventDELETE, eventPATCH, participantsDELETE, participantsPATCH } from './Api';
import { calendarGET, deleteAppointment } from './CalendarApi';
import { fetchInMatchAppointmentsMatch } from './matches/Api';

import ActiveMatches from './matches/available_matches/ActiveMatches';



const Schedules = ({ selectedDate }) => {
  const [appointments, setAppointments] = useState([]);
  const [selectedAppt, setSelectedAppt] = useState(null);
  const [localData, setLocalData] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [isParticipant, setIsParticipant] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showActiveMatchesModal, setShowActiveMatchesModal] = useState(false);
  const [activeMatchId, setActiveMatchId] = useState(null);
  const [activeMatchData, setActiveMatchData] = useState(null);

  const [isActiveMatchAvailable, setIsActiveMatchAvailable] = useState(false);

 const nowPH = new Date();

const todayDate = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Manila',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
}).format(nowPH).replace(/\//g, '-');

const currentTime = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Manila',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
}).format(nowPH);

const handleTimeChange = (field) => (e) => {
  const value = e.target.value;

  setLocalData((prev) => {
    const updated = { ...prev, [field]: value };

    const isToday = updated.event_date === todayDate;

    // validate start time
    if (field === 'start_time') {
      if (isToday && value < currentTime) {
        alert('Start time cannot be earlier than current time');
        return prev;
      }

      if (updated.end_time && value >= updated.end_time) {
        alert('Start time must be earlier than end time');
        return prev;
      }
    }

    // validate end time
    if (field === 'end_time') {
      if (updated.start_time && value <= updated.start_time) {
        alert('End time must be later than start time');
        return prev;
      }
    }

    return updated;
  });
};


  const loadActiveMatch = async (in_match_id) => {
    try {
      const data = await fetchInMatchAppointmentsMatch(in_match_id);
      setActiveMatchData(data); // store the full match object
      setShowActiveMatchesModal(true);
    } catch (err) {
      console.error('❌ Failed to fetch active match:', err);
    }
  };



  const fetchAppointments = async () => {
    try {
      const formattedDate = dayjs(selectedDate).format('YYYY-MM-DD');
      const data = await calendarGET(formattedDate);
      setAppointments(data);
    } catch (err) {
      console.error('❌ Failed to fetch appointments:', err);
    }
  };

  useEffect(() => {
    if (selectedDate) fetchAppointments();
  }, [selectedDate]);



  const handleView = (appt) => {
    if (appt.type === 'event') {
      const event = appt.event || {};
      const isOrganizer = event.is_organizer;
      const participantStatus = event.participant_status;
      const isParticipant = event.is_participant;

      let filteredParticipants = [];
      if (isOrganizer) {
        filteredParticipants = event.participants || [];
      } else if (participantStatus === 'approved') {
        filteredParticipants = (event.participants || []).filter(
          (p) => p.status === 'approved'
        );
      } else {
        filteredParticipants = (event.participants || []).filter(
          (p) => p.status === participantStatus
        );
      }

      setLocalData({
        type: 'event',
        event_name: event.event_name || '',
        event_date: event.event_date || '',
        start_time: event.time_range?.split('-')[0] || '',
        end_time: event.time_range?.split('-')[1] || '',
        description: event.description || '',
        venue: event.venue || '',
        address: event.address || '',
        permission: event.permission || 'none',
        max_participants: event.max_participants || 0,
        participants: filteredParticipants,
        isOrganizer,
        id: event.id,
      });

      setIsParticipant(isParticipant);
    } else if (appt.type === 'conversation') {
      setLocalData({
        type: 'conversation',
        target_id: appt.target_id,
        schedule_name: appt.schedule_name,
        date: appt.date,
        hour: appt.hour,
        conversation: appt.conversation || [],
      });
    } else if (appt.type === 'match') {
      // NEW: match logic
      setLocalData({
        type: 'match',
        target_id: appt.target_id,
        schedule_name: appt.schedule_name,
        date: appt.date,
        hour: appt.hour,
        match: appt.match || [],
      });

      // Fetch match details and check if valid
      fetchInMatchAppointmentsMatch(appt.target_id)
        .then((data) => {
          // If data is false, disable button
          setIsActiveMatchAvailable(!!data);
        })
        .catch((err) => {
          console.error("❌ Failed to fetch active match:", err);
          setIsActiveMatchAvailable(false);
        });


    }

    setSelectedAppt(appt);
    setShowViewModal(true);
  };


  const handleChange = (e) => {
    const { name, value } = e.target;
    setLocalData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDeleteClick = (appt) => {
    setSelectedAppt(appt);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      if (localData?.type === 'conversation' || localData?.type === 'match') {
        await deleteAppointment(localData.target_id, localData.type);  // same API as conversation
      } else if (localData?.isOrganizer) {
        await eventPATCH({ id: localData.id, delete: true });
      } else if (isParticipant) {
        await participantsDELETE(localData.id);
      }

      await fetchAppointments();
      setShowDeleteModal(false);
      setShowViewModal(false);
    } catch (err) {
      console.error('❌ Failed to delete:', err);
      alert('Failed to delete: ' + err.message);
    }
  };


  const truncateText = (text, maxLength = 20) => {
    if (!text) return '(Untitled)';
    return text.length > maxLength ? text.slice(0, maxLength) + '…' : text;
  };

  const isReadOnly = !localData?.isOrganizer;


  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);






  return (
    <Container className="p-0">


      <div
        className="w-100"
        style={{
          height: '250px',
          overflowY: 'auto',
          borderRadius: '10px',    // rounded corners
          display: 'flex',         // enable flexbox
          justifyContent: 'flex-start', // start vertically
          alignItems: 'center',    // center horizontally
          flexDirection: 'column', // stack children vertically

        }}
      >
        {appointments.length > 0 ? (

          appointments.map((appt) => {
            const name =
              appt.type === 'event'
                ? appt.event?.event_name
                : appt.schedule_name;

            return (

              <Row
                key={appt.appointment_id}
                className="mb-2 p-2 border"
                style={{
                  color: '#FFFAF0',
                 //borderColor: '#b84a13',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  width: '100%',
                  maxWidth: '100%',
                  backgroundColor: "#b84a13",
                }}
                onClick={() => handleView(appt)}
              >
                <Col>
                  {/* First line: Event name */}
                  <div
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={name || '(Untitled)'}
                  >
                    <strong>{truncateText(name)}</strong>
                  </div>

                  {/* Second line: Type aligned right */}
                  <div className="small text-white text-end">
                    {appt.type}
                  </div>
                </Col>
              </Row>




            );
          })
        ) : (
          <p style={{ color: '#740A03' }}>No appointments</p>
        )}
      </div>

      <Modal
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        size="lg"

        backdrop="static"
        keyboard={false}
      >

        <div
          style={{
           // background: 'linear-gradient(145deg, #e93701ff, rgba(248, 179, 29, 0.89))', // golden-red gradient
            borderRadius: '12px',  // optional: rounded corners
            padding: '16px',       // spacing inside modal
          }}
        >

          <Modal.Header closeButton   >
            <Modal.Title>Event Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {/* Event */}
            {localData && localData.type === "event" && (
              <>
                <Row className="mb-3 g-3">
                  {/* Event Name */}
                  <Col xs={12} md={8} className="d-flex flex-column">
                    <label className="form-label-sm mb-1">Event Name</label>
                    <input
                      type="text"
                      name="event_name"
                      value={localData.event_name}
                      onChange={handleChange}
                      className="form-control"
                      style={{
                        width: '100%',
                        height: '60px',
                        fontSize: '1.1rem',
                        padding: '0.5rem',
                      }}
                      readOnly={isReadOnly}
                    />
                  </Col>

                  {/* Event Date */}
                  <Col xs={12} md={4} className="d-flex flex-column">
                    <label className="form-label-sm mb-1">Event Date</label>
                    <DatePicker
                      selected={localData.event_date ? new Date(localData.event_date) : null}
                      onChange={(date) =>
                        setLocalData((prev) => ({
                          ...prev,
                          event_date: date ? date.toISOString().split("T")[0] : '',
                        }))
                      }
                      dateFormat="yyyy-MM-dd"
                      minDate={new Date()}
                      required
                      customInput={
                        <input
                          type="text"
                          className="form-control"
                          style={{
                            width: '100%',
                            height: '60px',
                            fontSize: '1.1rem',
                            padding: '0.5rem',
                          }}
                        />
                      }
                      readOnly={isReadOnly}
                      disabled={isReadOnly}
                    />
                  </Col>
                </Row>

                {localData && (
                  <>

                    <Row className="mb-2">
                      <Col xs="auto">
                        <label className="form-label form-label-sm me-1 mb-0">Start Time</label>
                        <input
                          className="form-control form-control-sm"
                          type="time"
                          name="start_time"
                          value={localData.start_time || ''}
    onChange={handleTimeChange('start_time')}
                          style={{ width: '200px' }}
                          readOnly={isReadOnly}
                          onClick={(e) => {
                            if (!isReadOnly) e.currentTarget.showPicker?.();
                          }}
                          disabled
                        />
                      </Col>



                      <Col xs="auto">
                        <label className="form-label form-label-sm me-1 mb-0">End Time</label>
                        <input
                          className="form-control form-control-sm"
                          type="time"
                          name="end_time"
                          value={localData.end_time || ''}
                              onChange={handleTimeChange('end_time')}

                          style={{ width: '200px' }}
                          readOnly={isReadOnly}
                          onClick={(e) => {
                            if (!isReadOnly) e.currentTarget.showPicker?.();
                          }}
                          disabled
                        />
                      </Col>
                    </Row>


                    <div className="mb-3">
                      <textarea
                        className="form-control form-control-sm"
                        name="description"
                        value={localData.description || ''}
                        onChange={handleChange}
                        style={{ height: '150px' }}
                        readOnly={isReadOnly}
                      />
                    </div>

                    <Row className="mb-3">
                      <Col xs="auto">
                        <label className="form-label form-label-sm mb-0 me-2">Max Participants</label>
                        <input
                          className="form-control form-control-sm"
                          type="number"
                          name="max_participants"
                          value={localData.max_participants || ''}
                          onChange={handleChange}
                          style={{ width: '150px' }}
                          readOnly={isReadOnly}
                        />
                      </Col>
                      <Col xs="auto">
                        <label className="form-label form-label-sm mb-0 me-2">Address</label>
                        <input
                          className="form-control form-control-sm"
                          name="address"
                          value={localData.address || ''}
                          onChange={handleChange}
                          style={{ width: '300px' }}
                          readOnly={isReadOnly}
                        />
                      </Col>
                      <Col xs="auto">
                        <label className="form-label form-label-sm mb-0 me-2">Venue</label>
                        <input
                          className="form-control form-control-sm"
                          name="venue"
                          value={localData.venue || ''}
                          onChange={handleChange}
                          style={{ width: '200px' }}
                          readOnly={isReadOnly}
                        />
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col xs="auto">
                        <label className="form-label-sm mb-0 me-2">Permission</label>
                        <select
                          className="form-select form-select-sm"
                          name="permission"
                          value={localData.permission || 'none'}
                          onChange={handleChange}
                          style={{ width: '200px' }}
                          disabled={isReadOnly}
                        >
                          <option value="none">None</option>
                          <option value="need_permission">Need Permission</option>
                        </select>
                      </Col>
                    </Row>

                    <Table size="sm" bordered hover>
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {localData.participants?.length > 0 ? (
                          localData.participants.map((p, idx) => (
                            <tr key={idx}>
                              <td>{p.full_name}</td>
                              <td>{p.email ?? 'Hidden'}</td>
                              <td>
                                {localData.isOrganizer ? (
                                  <select
                                    className="form-select form-select-sm"
                                    value={p.status}
                                    onChange={async (e) => {
                                      const newStatus = e.target.value;
                                      const old = p.status;
                                      // optimistic update
                                      try {
                                        const updated = [...localData.participants];
                                        updated[idx] = { ...updated[idx], status: newStatus };
                                        setLocalData((prev) => ({ ...prev, participants: updated }));
                                        await participantsPATCH(p.user_id, {
                                          event_id: localData.id,
                                          status: newStatus,
                                        });
                                      } catch (err) {
                                        // revert on error
                                        const reverted = [...localData.participants];
                                        reverted[idx] = { ...reverted[idx], status: old };
                                        setLocalData((prev) => ({ ...prev, participants: reverted }));
                                        const serverMessage = err.response?.data?.detail;
                                        alert('❌ Failed to  changes: ' + (serverMessage || err.message));
                                      }
                                    }}
                                  >
                                    <option value="pending">Pending</option>
                                    <option value="approved">Approved</option>
                                    <option value="rejected">Rejected</option>
                                  </select>
                                ) : (
                                  p.status
                                )}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={3} className="text-center text-muted">
                              No participants
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </>
                )}
              </>
            )}



            {/* Conversation */}
            {localData && localData.type === 'conversation' && (
              <div className="p-3" style={{ background: '#fefefe' }}>
                <div className="text-center mb-4">
                  <h3 style={{ fontWeight: '700', marginBottom: '5px' }}>
                    {localData.schedule_name}
                  </h3>
                  <small className="text-muted" style={{ fontSize: '1rem' }}>
                    {dayjs(localData.date).format('MMM D, YYYY')} • {localData.hour}
                  </small>
                </div>

                <h5 className="mb-4 text-center" style={{ fontWeight: '600' }}>
                  People in this conversation
                </h5>

                <div className="d-flex flex-wrap justify-content-center gap-4">
                  {localData.conversation.map((user, idx) => (
                    <div
                      key={idx}
                      className="text-center"
                      style={{
                        minWidth: 120,
                        padding: '12px',
                        borderRadius: '14px',
                        background: '#f8f9fa',
                      }}
                    >
                      <img
                        src={
                          user.profile_image
                            ? `${user.profile_image}?t=${Date.now()}`
                            : ''
                        }
                        alt={user.full_name}
                        style={{
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          objectFit: 'cover',
                          marginBottom: '10px',
                        }}
                      />
                      <div style={{ fontSize: '1rem', fontWeight: '600' }}>
                        {user.full_name}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}


            {/* Match */}
            {localData && localData.type === 'match' && (
              <div className="p-3" style={{ background: '#fefefe' }}>
                <div className="text-center mb-4">
                  <h3 style={{ fontWeight: '700', marginBottom: '5px' }}>
                    {localData.schedule_name}
                  </h3>
                  <small className="text-muted" style={{ fontSize: '1rem' }}>
                    {dayjs(localData.date).format('MMM D, YYYY')} • {localData.hour}
                  </small>
                </div>

                <h5 className="mb-4 text-center" style={{ fontWeight: '600' }}>
                  Matched People
                </h5>

                <div className="d-flex flex-wrap justify-content-center gap-4">
                  {localData.match.map((user, idx) => (
                    <div
                      key={idx}
                      className="text-center"
                      style={{
                        minWidth: 120,
                        padding: '12px',
                        borderRadius: '14px',
                        background: '#f8f9fa',
                      }}
                    >
                      <img
                        src={
                          user.profile_image
                            ? `${user.profile_image}?t=${Date.now()}`
                            : `https://ui-avatars.com/api/?name=${encodeURIComponent(user.full_name || "User")}&size=80&background=ddd&color=555`
                        }
                        alt={user.full_name}
                        style={{
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          objectFit: 'cover',
                          marginBottom: '10px',
                        }}
                      />

                      <div style={{ fontSize: '1rem', fontWeight: '600' }}>
                        {user.full_name}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Add Active Matches Button */}
                <div className="text-center mt-3">

                  <Button
                    variant="success"
                    onClick={() => loadActiveMatch(selectedAppt?.target_id)}
                    disabled={!isActiveMatchAvailable} // now correctly disabled if API returns "error"
                  >
                    Active Matches
                  </Button>




                </div>
              </div>
            )}


          </Modal.Body>

          <Modal.Footer>



            {localData?.type === 'event' &&
              !localData?.isOrganizer &&
              isParticipant && (
                <Button
                  variant="danger"
                  onClick={async () => {
                    try {
                      await participantsDELETE(localData.id);
                      await fetchAppointments();
                      setShowViewModal(false);
                    } catch (err) {
                      console.error('❌ Failed to leave event:', err);
                      alert('Failed to leave event: ' + err.message);
                    }
                  }}
                >
                  Leave Event
                </Button>
              )}

            {localData?.isOrganizer && (
              <div>
                <Button
                  variant="primary"
                 onClick={async () => {
  try {
    const now = new Date();

    const todayDate = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Manila',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(now).replace(/\//g, '-');

    const currentTime = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Manila',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(now);

    const isToday = localData.event_date === todayDate;

    if (!localData.start_time || !localData.end_time) {
      alert('Start and end time are required');
      return;
    }

    if (isToday && localData.start_time < currentTime) {
      alert('Start time cannot be in the past');
      return;
    }

    if (localData.end_time <= localData.start_time) {
      alert('End time must be later than start time');
      return;
    }

    const payload = {
      id: localData.id,
      event_name: localData.event_name,
      event_date: localData.event_date,
      time_range: `${localData.start_time}-${localData.end_time}`,
      description: localData.description,
      venue: localData.venue,
      max_participants: Number(localData.max_participants),
      permission: localData.permission,
      address: localData.address,
    };

    await eventPATCH(payload);
    await fetchAppointments();
    setShowViewModal(false);
  } catch (err) {
    const serverMessage = err.response?.data?.detail;
    alert('Failed to changes: ' + (serverMessage || err.message));
  }
}}
                >
                  Save Change
                </Button>

                <Button variant="danger" onClick={() => setShowDeleteConfirm(true)}>Delete</Button>

              </div>
            )}





            {(localData?.type === 'conversation' || localData?.type === 'match') && (
              <Button
                variant="danger"
                onClick={() => handleDeleteClick(selectedAppt)}
              >
                Delete Appointment
              </Button>
            )}

          </Modal.Footer>








        </div>
      </Modal>


      {/* Active Matches Modal */}
      <Modal
        show={showActiveMatchesModal}
        onHide={() => setShowActiveMatchesModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Active Match</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {activeMatchData && (
            <ActiveMatches
              matchData={activeMatchData}
              onClose={async () => {
                setShowActiveMatchesModal(false);

                // refetch the active match
                if (selectedAppt?.target_id) {
                  try {
                    const data = await fetchInMatchAppointmentsMatch(selectedAppt.target_id);
                    setActiveMatchData(data); // update state
                    setIsActiveMatchAvailable(!!data); // update availability
                    await fetchAppointments(); // also refresh the appointments list if needed
                  } catch (err) {
                    console.error("❌ Failed to refresh active match:", err);
                  }
                }
              }}
            />

          )}
        </Modal.Body>
      </Modal>




      <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-danger mb-0">
            ⚠️ Are you sure you want to permanently delete the event{' '}
            <strong>{localData?.event_name}</strong>?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={async () => {
              try {
                await eventDELETE({ id: localData.id });
                setAppointments(prev => prev.filter(ev => ev.id !== localData.id));
                setShowDeleteConfirm(false);
                setShowViewModal(false);
              } catch (err) {
                console.error('❌ Error deleting event:', err);
              }
            }}
          >
            Yes, Delete
          </Button>
        </Modal.Footer>
      </Modal>


      {/* Delete Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this appointment? This action cannot be
          undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            Yes, Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Schedules;
